import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import Header from '../../Header'
import Footer from '../../Footer'
import Keyboard, { HIDDEN_KEYBOARD_KEYS_LICENCE_PLATE } from '../../Keyboard'
import { AppState } from '../../../reducers'
import { getReservationByNumberAndLicencePlate } from '../../../actions'
import { setCarLicencePlateAction } from '../../../actions/reservationChange'
import { CustomCaretProps } from '../../CustomCaret'
import './licencePlate.less'
import FooterMobile from '../../FooterMobile'
import { getMode } from '../../../hooks/mode'
import { MODE } from '../../../constants/mode'
import { FormFieldInput } from '../../FormFieldInput'
import { INPUT_TYPE } from '../../../constants/inputTypes'

const mapDispatchToProps = {
  setCarLicencePlate: setCarLicencePlateAction,
  getReservationByLicencePlate: getReservationByNumberAndLicencePlate,
}

type DispatchProps = typeof mapDispatchToProps

const mapStateToProps = (state: AppState) => ({
  carLicencePlate: state.reservationChange.carLicencePlate,
})

type StateProps = ReturnType<typeof mapStateToProps>
type Props = StateProps & DispatchProps

interface CustomMobileProps {
  licencePlateInput: React.MutableRefObject<any>
  onChange: (value: string) => void
  getReservation: () => void
}

type MobileLicencePlateProps = CustomMobileProps & Pick<Props, 'carLicencePlate'>

const MobileLicencePlate: React.FC<MobileLicencePlateProps> = ({ licencePlateInput, carLicencePlate, onChange, getReservation }) => {
  const { t } = useTranslation()

  const onKeyboardDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 13 && carLicencePlate.trim()) {
      getReservation()
    }
  }

  return (
    <div className="licence-plate-mobile">
      <div className="licence-plate-mobile-wrapper">
        <FormFieldInput
          id="licence-plate-input"
          type={INPUT_TYPE.LICENCE_PLATE}
          label={t('confirmLicencePlate')}
          sublabel={t('useOnlyLettersAndNumbers')}
          autoFocus={!carLicencePlate}
          inputRef={licencePlateInput}
          value={carLicencePlate}
          onChange={onChange}
          onKeyDown={onKeyboardDown}
        />
        <div className="next-button">
          <button
            className="main-button-mobile"
            onClick={getReservation}
            disabled={!carLicencePlate || !carLicencePlate.trim()}
          >
            {t('next')}
          </button>
        </div>
      </div>
      <div className="footer-mobile-container">
        <FooterMobile />
      </div>
    </div>
  )
}

interface CustomDesktopProps {
  licencePlateInput: React.MutableRefObject<any>
  onChange: (value: string) => void
  setExpanded: (value: boolean) => void
  updateCaretPosition: () => void
  refLicencePlateCaret: React.RefObject<CustomCaretProps>
  getReservation: () => void
  expanded: boolean
  activeInput: HTMLInputElement | undefined
  mode: MODE
}

type DesktopLicencePlateProps = CustomDesktopProps & Pick<Props, 'carLicencePlate'>

const DesktopLicencePlate: React.FC<DesktopLicencePlateProps> = ({ licencePlateInput, carLicencePlate, onChange, setExpanded, updateCaretPosition, refLicencePlateCaret, getReservation, expanded, activeInput, mode }) => {
  const { t } = useTranslation()
  const isKioskMode = mode === MODE.KIOSK
  return (
    <React.Fragment>
      <Header />
      <div className="licence-plate">
        <FormFieldInput
          id="licence-plate-input"
          type={INPUT_TYPE.LICENCE_PLATE}
          label={t('confirmLicencePlate')}
          sublabel={t('useOnlyLettersAndNumbers')}
          autoFocus
          inputRef={licencePlateInput}
          value={carLicencePlate}
          onChange={onChange}
          onClick={() => {
            setExpanded(true)
            updateCaretPosition()
          }}
          caretRef={refLicencePlateCaret}
        />
        <div className="next-button">
          <button
            className="main-button"
            onClick={getReservation}
            disabled={!carLicencePlate || !carLicencePlate.trim()}
          >
            {t('next')}
          </button>
        </div>
      </div>

      {isKioskMode && <div className="keyboard-cont">
        {expanded && (
          <Keyboard
            input={activeInput}
            onChange={onChange}
            onClose={() => setExpanded(false)}
            hiddenKeys={HIDDEN_KEYBOARD_KEYS_LICENCE_PLATE}
          />
        )}
      </div>}
      <div className="footer-cont">{(!expanded || !isKioskMode) && <Footer />}</div>
    </React.Fragment>
  )
}

const LicencePlateAuth: React.FC<Props> = ({
  carLicencePlate,
  setCarLicencePlate,
  getReservationByLicencePlate,
}: Props) => {
  const licencePlateInput = useRef<HTMLInputElement | null>(null)
  const [activeInput, setActiveInput] = useState<HTMLInputElement>()
  const [expanded, setExpanded] = useState(true)
  const refLicencePlateCaret = useRef<CustomCaretProps>(null)
  const mode = getMode()

  const updateCaretPosition = () => {
    refLicencePlateCaret.current && refLicencePlateCaret.current.updateCaretPosition()
  }

  const getReservation = () => {
    getReservationByLicencePlate(carLicencePlate)
  }

  const onChange = (value: string) => {
    setCarLicencePlate(value)
    updateCaretPosition()
  }

  useEffect(() => {
    if (licencePlateInput && licencePlateInput.current) {
      setActiveInput(licencePlateInput.current)
    }
  }, [])

  if (mode === MODE.MOBILE) {
    const mobileProps = { licencePlateInput, carLicencePlate, onChange, getReservation }
    return <MobileLicencePlate {...mobileProps} />
  } else {
    const desktopProps = { licencePlateInput, carLicencePlate, onChange, setExpanded, updateCaretPosition, refLicencePlateCaret, getReservation, expanded, activeInput, mode }
    return <DesktopLicencePlate {...desktopProps} />
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(LicencePlateAuth)
